import React, { useState, useEffect } from "react";
import { Drawer, Box, Typography, IconButton, TextField, CircularProgress, List, ListItem, ListItemText, ListItemSecondaryAction, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from "../../../../reducers/settingsApi";
import { toast } from "react-toastify";

const BlacklistIpsDrawer = ({ open, onClose, onUpdate }) => {
    const settingKey = "app.setting.backlistIp";
    const { data: settingRes, isLoading: isFetching } = useGetSettingByKeyQuery(settingKey);
    const [upsertSetting, { isLoading: isUpdating }] = useUpsertSettingMutation();

    const [ips, setIps] = useState([]);
    const [newIp, setNewIp] = useState("");

    useEffect(() => {
        if (settingRes?.data?.value) {
            setIps(Array.isArray(settingRes.data.value) ? settingRes.data.value : [settingRes.data.value]);
        }
    }, [settingRes, open]);

    const updateSettings = async (updatedIps) => {
        try {
            const payload = {
                key: settingKey,
                value: updatedIps,
                description: "List of map Ips",
                category: "app",
                isPublic: true
            };

            await upsertSetting(payload).unwrap();
            setIps(updatedIps);
            setNewIp("");
            toast.success("Blacklisted IPs updated successfully");
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Failed to save IPs:", error);
            toast.error(error?.data?.message || "Failed to update Blacklist IPs");
        }
    };

    const handleRemoveIp = async (ipToRemove) => {
        const updatedIps = ips.filter(ip => ip !== ipToRemove);
        await updateSettings(updatedIps);
    };

    const handleAddIp = async () => {
        let updatedIps = [...ips];
        const trimmed = newIp.trim();

        if (trimmed) {
            const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            if (!ipRegex.test(trimmed)) {
                toast.error("Please enter a valid IP address");
                return;
            }

            if (updatedIps.includes(trimmed)) {
                toast.warning("IP already exists in the list");
                return;
            } else {
                updatedIps = [...updatedIps, trimmed];
            }
        } else {
            return;
        }

        await updateSettings(updatedIps);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                "& .MuiDrawer-paper": {
                    width: "450px",
                    top: "0px !important",
                    height: "100%",
                    p: 0,
                    backgroundColor: "#fafafa"
                }
            }}
        >
            <Box sx={{ p: 0 }}>
                <Box className="d-flex justify-content-between align-items-center px-3 py-3 bg-white" style={{ borderBottom: "1px solid #EEEEEE" }}>
                    <Typography className="sofiaBold" style={{ fontSize: "18px", color: "#1D1D1B" }}>
                        Manage Blacklist IPs
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ backgroundColor: "#F5F5F5" }}>
                        <CloseIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography className="sofiaRegular" style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                        Enter an IP address and add it to the global restriction list. These IPs will be blocked based on routing rules.
                    </Typography>

                    <Box sx={{ mb: 2, display: "flex", gap: "10px" }}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Add New IP Address"
                            placeholder="e.g. 192.168.1.1"
                            value={newIp}
                            onChange={(e) => setNewIp(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddIp()}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#FFFFFF"
                                }
                            }}
                        />
                        <button
                            disabled={isUpdating}
                            onClick={handleAddIp}
                            className="sofiaBold px-3 bg-white"
                            style={{ border: "1px solid #1D1D1B", borderRadius: "10px", fontSize: "14px", cursor: isUpdating ? "not-allowed" : "pointer", minWidth: "80px" }}
                        >
                            {isUpdating ? "Saving..." : "Add"}
                        </button>
                    </Box>

                    <Box className="bg-white" style={{ borderRadius: "10px", border: "1px solid #EEEEEE" }}>
                        {isFetching ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                <CircularProgress size={24} sx={{ color: "#1D1D1B" }} />
                            </Box>
                        ) : (
                            <List sx={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto", p: 0 }}>
                                {ips.length === 0 ? (
                                    <Typography className="sofiaRegular" sx={{ textAlign: "center", py: 4, color: "#999", fontStyle: "italic", fontSize: "14px" }}>
                                        No restricted IPs added yet.
                                    </Typography>
                                ) : (
                                    ips.map((ip, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={ip}
                                                    primaryTypographyProps={{ className: "sofiaBold", style: { fontSize: "14px", color: "#1D1D1B" } }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton edge="end" onClick={() => handleRemoveIp(ip)} style={{ color: "#FF1F1F" }}>
                                                        <DeleteIcon sx={{ fontSize: "20px" }} />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < ips.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        )}
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default BlacklistIpsDrawer;
